/* eslint-disable no-unused-vars */
/* eslint-disable no-prototype-builtins */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayTrigger, Form, Tooltip, Alert, Row, Button } from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import { LoadOrders, LoadOrderLines, orderAction } from '../../../../features/Order/OrderSlice';
import { loaderActions } from '../../../../features/Loader/LoaderSlice';
import OrderLine from '../../../../components/shared/OrderLine/Index';
import API from '../../../../Services/API';
import UploadSingleOrderLines from '../../../../components/shared/Files/UploadSingleOrderLines';
import { downloadPurchaseOrderLines } from '../../../../Helpers/xlxs/download';
import Swal from 'sweetalert2';
import { formatDate } from '../../../../Helpers/date';
import { validatePurchaseLine } from '../../../../Helpers/line.validation';
import {
  useCurrentCompanyFullName,
  useCurrentCompanyID
} from '../../../../hooks/useCurrentCompany';
import { sortLinesByErrors } from '../../../../Helpers/sort';
import { CheckRequireApproval } from '../../../../Helpers/requireApproval';
import Purchase from '../../../../components/shared/Order/Purchase';
import { isPOEligibleToPost } from '../../../../Helpers/postable';

function PurchaseOrderId() {
  const { id, vendorType } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (vendorType.toLowerCase() !== 'warehouse' && vendorType.toLowerCase() !== 'supplier') {
      navigate(`/purchase-orders${location.search}`);
    }
  }, [vendorType]);

  const { currentOrder, linesCount, orderLines } = useSelector((state) => state.order);
  const { currentVendor } = useSelector((state) => state.vendor);
  const { isFeedBackLoopEnabled } = useSelector((state) => state.general);

  const [search, setSearch] = useState('');
  const [editedLines, setEditedLines] = useState([]);
  const [savableLines, setSavableLines] = useState([]);
  const [requireApprovalLines, setRequireApprovalLines] = useState([]);
  const [notApprovedLines, setNotApprovedLines] = useState([]);
  const [filteredLines, setFilteredLines] = useState([]);
  const [downloadableLines, setDownloadableLines] = useState(0);
  const companyId = useCurrentCompanyID();
  const companyFullName = useCurrentCompanyFullName();

  function refreshPage() {
    fetchCurrentOrder();
  }

  async function fetchCurrentOrder() {
    await dispatch(loaderActions.show());
    await dispatch(
      LoadOrders({
        endpoint: '/purchase_order_header',
        companyId,
        filter: `docNo eq '${id}'`,
        type: 'single'
      })
    );
  }

  async function fetchCurrentOrderLines() {
    await dispatch(loaderActions.show());
    await dispatch(
      LoadOrderLines({
        endpoint: '/purchase_order_lines',
        companyId,
        filter: `documentNo eq '${id}'`,
        implementPaginate: false
      })
    );
    return await dispatch(loaderActions.hide());
  }

  useEffect(() => {
    setTimeout(() => {
      fetchCurrentOrder();
    }, 100);
  }, [id]);

  useEffect(() => {
    setTimeout(async () => {
      if (currentOrder !== undefined) {
        await fetchCurrentOrderLines();
      } else {
        toast.error('No PO found with Order number ' + id);
        navigate('/purchase-orders');
        await dispatch(loaderActions.hide());
      }
    }, 100);
  }, [currentOrder]);

  useEffect(() => {
    const filtered = orderLines.filter(
      (order) =>
        order.itemNo.toLowerCase().includes(search.trim().toLowerCase()) ||
        order.description.toLowerCase().includes(search.trim().toLowerCase())
    );
    setFilteredLines(filtered);
  }, [orderLines, search]);

  useEffect(() => {
    let downloadableLinesList = orderLines;
    if (isFeedBackLoopEnabled === true) {
      downloadableLinesList = orderLines.filter(
        (line) => line.feedbackStatus.trim() === '' || line.feedbackStatus === 'Rejected'
      );
    }
    setDownloadableLines(downloadableLinesList.length);

    const editedLines = orderLines.reduce(
      (acc, line) => acc + (line.isEditing === true ? 1 : 0),
      0
    );
    setEditedLines(editedLines);

    const savableLines = orderLines.filter((line) => line.isEditing);
    setSavableLines(savableLines);

    const requireApprovalLines = isFeedBackLoopEnabled
      ? orderLines.filter((line) => {
          return CheckRequireApproval(line, isFeedBackLoopEnabled) === true;
        })
      : [];

    setRequireApprovalLines(requireApprovalLines);

    const notApprovedLines = isFeedBackLoopEnabled
      ? orderLines.filter(
          (line) => line.feedbackStatus !== 'Approved' && line.feedbackStatus.trim() !== ''
        )
      : [];

    setNotApprovedLines(notApprovedLines);
  }, [orderLines]);

  async function editValue(documentNo, lineNo, targetField) {
    const targetKey = Object.keys(targetField)[0];
    dispatch(
      orderAction.updateOrderLine({
        documentNo,
        lineNo,
        targetField
      })
    );
    if (targetKey === 'whseConfirmQty') {
      let qtyMissing = 0;
      if (targetField[targetKey] > 0) {
        const targetLine = await orderLines.find((line) => line.lineNo === lineNo);
        qtyMissing =
          targetLine.vendorConfirmQty - (targetLine.qtyReceived + targetField[targetKey]);
        if (qtyMissing < 0) {
          qtyMissing = 0;
        }
      }
      dispatch(
        orderAction.updateOrderLine({
          documentNo,
          lineNo,
          targetField: { qtyMissing }
        })
      );
    }
  }

  const orderLinesList = filteredLines.map((line, index) => {
    return (
      <OrderLine
        documentNo={line.documentNo}
        line={line}
        index={index}
        vendorType={vendorType}
        key={index}
        type="purchase"
        refresh={refreshPage}
        editValue={editValue}
        showSaveButton={true}
        showOrderLink={false}
        orderLines={orderLines}
      />
    );
  });

  async function saveLines(actionType) {
    if (actionType.toLowerCase() === 'save' && savableLines.length === 0) {
      toast.error('There are no valid PO lines that can be Save', { id: 'save-toast' });
      return;
    }
    const validatableLines = savableLines.length > 0 ? savableLines : orderLines;
    const validatedLines = validatableLines.map((line) =>
      validatePurchaseLine(line, vendorType, orderLines, isFeedBackLoopEnabled)
    );
    dispatch(orderAction.updateMultipleOrderLines(sortLinesByErrors(validatedLines)));
    if (validatedLines.filter((line) => !line.isValid).length === 0) {
      let result;
      let showPopup = false;

      if (vendorType === 'Warehouse') {
        const quantityConfirmation = validatedLines.filter((line) => {
          return CheckRequireApproval(line, isFeedBackLoopEnabled) === true;
        });
        if (quantityConfirmation.length > 0) {
          showPopup = true;
        }
      }

      if (isFeedBackLoopEnabled && showPopup) {
        result = await Swal.fire({
          title: 'The amount is over and the PO will need to be approved',
          text: "Please make sure everything is uploaded properly, You won't be able to revert this!",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: `Yes, ${actionType} it!`,
          customClass: {
            title: 'swal2-title-red'
          }
        });
      } else {
        result = await Swal.fire({
          title: 'Are you sure?',
          text: "Please make sure everything is uploaded properly, You won't be able to revert this!",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: `Yes, ${actionType} it!`
        });
      }
      if (result.isConfirmed) {
        dispatch(loaderActions.show());
        const batchRequestData = savableLines.map((line) => {
          const data = {};
          line.editedFields.forEach((field) => {
            if (field === 'qtyReceived') {
              data.whseConfirmQty = Number(line[field]);
            } else {
              data[field] = line[field];
            }
          });
          return {
            method: 'PATCH',
            url: `purchaseOrderLinesVP('${line.documentNo}',${line.lineNo},'${line.itemNo}')`,
            headers: {
              Company: companyFullName,
              'Content-Type': 'application/json',
              'If-Match': '*'
            },
            body: data
          };
        });
        if (batchRequestData.length > 0) {
          const res = await API().post('/purchase-batch', { requests: batchRequestData });
          const errors = [];
          res.data.responses.forEach((resp) => {
            if (resp.body && typeof resp.body === 'object' && resp.body.error) {
              errors.push(resp.body.error.message);
            }
          });
          if (errors.length > 0) {
            errors.map((err) => toast.error(err));
          } else if (actionType.toLowerCase() === 'save') {
            toast.success('Order lines saved successfully');
          }
        }
        if (actionType.toLowerCase() === 'post') {
          if (
            currentOrder.hasOwnProperty('scheduledForPosting') &&
            currentOrder.scheduledForPosting === true
          ) {
            if (batchRequestData.length > 0) {
              refreshPage();
            }
            dispatch(loaderActions.hide());
            return Swal.fire({
              icon: 'error',
              title: 'Oops...',
              text: "Your PO is being processed and will be posted soon. Please check back later.\n\nOnce it's posted, it will no longer appear on EVP."
            });
          }
          const res = await API().post(`/warehouse_post?(${companyId})?('${id}')`, {
            postDate: formatDate(new Date())
          });
          if (res.data && typeof res.data === 'object' && res.data.error) {
            toast.error(res.data.error.message);
          } else if (res.data && typeof res.data === 'object' && res.data.StatusCode !== 200) {
            toast.error(res.data.Message);
          } else if (
            res.response &&
            res.response.data &&
            res.response.data.error &&
            typeof res.response.data.error === 'object'
          ) {
            toast.error(res.response.data.error.message);
          } else if (res.response && res.response.status >= 400) {
            toast.error(res.response.data.message || res.response.data.Message);
          } else if (res.code === 'ERR_NETWORK') {
            toast.error(res.message);
          } else {
            navigate(`/purchase-orders${location.search}`);
            toast.success('Order lines posted successfully');
          }
        }
        if (batchRequestData.length > 0) {
          refreshPage();
        }
        dispatch(loaderActions.hide());
      }
    } else {
      toast.error('Please fix all invalid inputs');
    }
  }

  const ErrorCard = (
    <>
      <Alert variant="warning">
        <> No Purchase order lines for {id}</>
      </Alert>
    </>
  );

  const SearchErrorCard = (
    <>
      <Alert variant="warning">
        <> No Purchase order lines matching current search</>
      </Alert>
    </>
  );

  function downloadXlxs() {
    let lines = orderLines;
    if (isFeedBackLoopEnabled === true) {
      lines = orderLines.filter(
        (line) => line.feedbackStatus.trim() === '' || line.feedbackStatus === 'Rejected'
      );
    }
    if (vendorType.toLowerCase() === 'supplier') {
      lines = orderLines.filter((line) =>
        [
          'Confirmation Sent to Vendor',
          'Confirmed by Vendor',
          'Confirmation Sent to Warehouse'
        ].includes(line.vendorPortalStatusCaption)
      );
    }
    downloadPurchaseOrderLines(
      lines,
      `${currentOrder.docNo}-PO-Lines-${vendorType.toLowerCase()}`,
      vendorType.toLowerCase(),
      currentVendor.vpFreightType
    );
  }

  const UpdatedableFromSupplier = orderLines.some((line) =>
    [
      'Confirmation Sent to Vendor',
      'Confirmed by Vendor',
      'Confirmation Sent to Warehouse'
    ].includes(line.vendorPortalStatusCaption)
  );

  const UpdatedableFromWarehouse = orderLines.some((line) =>
    [
      'Confirmation Sent to Vendor',
      'Confirmed by Vendor',
      'Confirmation Sent to Warehouse',
      'Confirmed by Warehouse'
    ].includes(line.vendorPortalStatusCaption)
  );

  const showMassButtons =
    vendorType.toLowerCase() === 'warehouse' ? UpdatedableFromWarehouse : UpdatedableFromSupplier;

  return (
    <div>
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div>Purchase order detail</div>
          {currentOrder && Object.keys(currentOrder).length > 0 && (
            <Purchase order={currentOrder} vendorType={vendorType} isDetailPage={true} />
          )}
        </div>
      </div>
      {orderLines.length > 0 && (
        <div className=" shadow-sm mb-4">
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <div className="mb-3 d-flex justify-content-between">
                <div>
                  <span>
                    Order Lines ( {filteredLines.length} / {linesCount} )
                  </span>
                  <div className="mt-3 pl-3">
                    <Row>
                      {savableLines.length > 0 && (
                        <Button variant="info" onClick={() => saveLines('Save')}>
                          Save Lines
                        </Button>
                      )}
                      {isPOEligibleToPost(
                        vendorType,
                        currentOrder,
                        orderLines,
                        requireApprovalLines,
                        notApprovedLines
                      ) ? (
                        <Button
                          variant="primary"
                          className={'ml-2'}
                          onClick={() => saveLines('Post')}
                        >
                          Post PO
                        </Button>
                      ) : (
                        <></>
                      )}
                      {currentOrder.scheduledForPosting && (
                        <div className="text-warning">
                          PO is scheduled for posting, editing is disabled.
                        </div>
                      )}
                    </Row>
                  </div>
                </div>
                <div className="d-flex">
                  <Form.Control
                    size="sm"
                    type="text"
                    className="mr-2"
                    placeholder="Search "
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {showMassButtons && downloadableLines > 0 && (
                    <UploadSingleOrderLines
                      type="purchase"
                      vendorType={vendorType}
                      documentNo={id}
                      refresh={refreshPage}
                      orderLines={orderLines}
                      status={
                        currentOrder && Object.keys(currentOrder).length ? currentOrder.status : ''
                      }
                      currentOrder={currentOrder}
                    />
                  )}
                  {showMassButtons && downloadableLines > 0 && (
                    <OverlayTrigger
                      key="download"
                      placement={'top'}
                      overlay={<Tooltip id={`tooltip-download`}>Download </Tooltip>}
                    >
                      <i
                        className="feather icon-download  ml-2 mt-2"
                        type="button"
                        style={{ width: '48px', height: '48px' }}
                        onClick={downloadXlxs}
                      />
                    </OverlayTrigger>
                  )}
                </div>
              </div>
              {currentOrder &&
                vendorType.toLowerCase() === 'warehouse' &&
                currentOrder.hasOwnProperty('jobQueueErrorMessage') &&
                currentOrder.jobQueueErrorMessage !== '' && (
                  <Alert variant="danger">{currentOrder.jobQueueErrorMessage}</Alert>
                )}
            </div>
          </div>
        </div>
      )}
      <div>
        <div>
          {filteredLines.length > 0
            ? orderLinesList
            : orderLines.length > 0
            ? SearchErrorCard
            : ErrorCard}
        </div>
      </div>
    </div>
  );
}

export default PurchaseOrderId;
